import React, { useContext, useRef } from 'react';
import { AriaCheckboxProps } from '@react-types/checkbox';
import { MultiSelectStateContext } from './PrimitiveMultiSelect';
import { MultiSelectState } from './MultiSelect.types';
import { useToggleState } from 'react-stately';
import { twMerge } from 'tailwind-merge';
import { Check } from 'lucide-react';

export interface MultiSelectAllCheckboxAria {
  /** Props for the select all checkbox element. */
  checkboxProps: AriaCheckboxProps;
}

export function useMultiSelectAllCheckbox<T>(
  state: MultiSelectState<T> | null,
): MultiSelectAllCheckboxAria {
  const { isEmpty, isSelectAll, selectionMode } = state?.selectionManager ?? {};

  return {
    checkboxProps: {
      'aria-label': selectionMode === 'single' ? 'select' : 'selectAll',
      isSelected: isSelectAll,
      isDisabled: selectionMode !== 'multiple' || state?.collection.size === 0,
      isIndeterminate: !isEmpty && !isSelectAll,
      onChange: () => state?.selectionManager.toggleSelectAll(),
    },
  };
}

export const MultiSelectAllCheckbox = () => {
  const inputRef = useRef(null);
  const state = useContext(MultiSelectStateContext);
  const isSingleSelectionMode = state?.selectionManager.selectionMode === 'single';
  const { checkboxProps } = useMultiSelectAllCheckbox(state);
  const { toggle } = useToggleState(checkboxProps);
  return (
    <div
      onClick={toggle}
      ref={inputRef}
      className={twMerge(
        'group flex items-center gap-2.5 cursor-default select-none py-4 px-4 pr-1 outline outline-0 text-sm forced-color-adjust-none w-full hover:bg-accent hover:text-accent-foreground',
        isSingleSelectionMode && 'hidden',
      )}>
      <span
        className={twMerge(
          'w-3.5 h-3.5 flex items-center rounded-full justify-center',
          checkboxProps.isSelected ? 'bg-green-400' : 'bg-zinc-300',
        )}>
        <Check className="w-2.5 h-2.5 text-white" />
      </span>
      <span className="flex-1 flex items-center gap-2 truncate font-normal text-xs group-selected:font-semibol rounded-full text-zinc-800">
        Select all
      </span>
    </div>
  );
};
